import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Modal,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useDispatch, useSelector } from 'react-redux';

import { useTheme } from '../../theme/ThemeContext';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorMessage } from '../common/ErrorMessage';
import { Button } from '../common/Button';
import type { AppDispatch } from '../../store';
import {
  generateCollaborativeLearningActivities,
  selectCollaborativeLearningActivities,
  selectGroupActivitiesByType,
  selectStudyGroupLoading,
  selectStudyGroupError,
  addActivityToGroup,
  type CollaborativeLearningActivity,
} from '../../store/slices/studyGroupSlice';

interface CollaborativeLearningActivitiesProps {
  groupId: string;
  memberSkills: Record<string, string[]>;
  learningGoals: string[];
  timeConstraints: number;
  onActivitySelect: (activity: CollaborativeLearningActivity) => void;
  onActivityStart: (activityId: string) => void;
  showGenerateButton?: boolean;
}

export const CollaborativeLearningActivities: React.FC<
  CollaborativeLearningActivitiesProps
> = ({
  groupId,
  memberSkills,
  learningGoals,
  timeConstraints,
  onActivitySelect,
  onActivityStart,
  showGenerateButton = true,
}) => {
  const { theme } = useTheme();
  const dispatch = useDispatch<AppDispatch>();

  const activities = useSelector(selectCollaborativeLearningActivities);
  const activitiesByType = useSelector(selectGroupActivitiesByType);
  const loading = useSelector(selectStudyGroupLoading);
  const error = useSelector(selectStudyGroupError);

  const [selectedType, setSelectedType] = useState<string>('all');
  const [showActivityDetails, setShowActivityDetails] = useState<string | null>(
    null
  );
  const [generatingActivities, setGeneratingActivities] = useState(false);

  const activityTypes = [
    { key: 'all', label: 'All Activities', icon: 'grid-outline' },
    { key: 'discussion', label: 'Discussions', icon: 'chatbubbles-outline' },
    { key: 'practice_session', label: 'Practice', icon: 'fitness-outline' },
    { key: 'challenge', label: 'Challenges', icon: 'trophy-outline' },
    { key: 'review', label: 'Reviews', icon: 'checkmark-circle-outline' },
    { key: 'project', label: 'Projects', icon: 'construct-outline' },
  ];

  useEffect(() => {
    if (activities.length === 0) {
      handleGenerateActivities();
    }
  }, [groupId]);

  const handleGenerateActivities = async () => {
    try {
      setGeneratingActivities(true);
      await dispatch(
        generateCollaborativeLearningActivities({
          groupId,
          memberSkills,
          learningGoals,
          timeConstraints,
        })
      ).unwrap();
    } catch (err) {
      Alert.alert(
        'Error',
        'Failed to generate collaborative learning activities. Please try again.'
      );
    } finally {
      setGeneratingActivities(false);
    }
  };

  const getDifficultyColor = (difficulty: number) => {
    if (difficulty <= 0.3) return theme.colors.success;
    if (difficulty <= 0.7) return theme.colors.warning;
    return theme.colors.error;
  };

  const getDifficultyLabel = (difficulty: number) => {
    if (difficulty <= 0.3) return 'Easy';
    if (difficulty <= 0.7) return 'Medium';
    return 'Hard';
  };

  const getActivityTypeIcon = (type: CollaborativeLearningActivity['type']) => {
    switch (type) {
      case 'discussion':
        return 'chatbubbles';
      case 'practice_session':
        return 'fitness';
      case 'challenge':
        return 'trophy';
      case 'review':
        return 'checkmark-circle';
      case 'project':
        return 'construct';
      default:
        return 'help-circle';
    }
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return remainingMinutes > 0
      ? `${hours}h ${remainingMinutes}m`
      : `${hours}h`;
  };

  const getFilteredActivities = () => {
    if (selectedType === 'all') return activities;
    return activities.filter(activity => activity.type === selectedType);
  };

  const renderActivityItem = ({
    item,
  }: {
    item: CollaborativeLearningActivity;
  }) => {
    const difficultyColor = getDifficultyColor(item.difficulty);
    const difficultyLabel = getDifficultyLabel(item.difficulty);

    return (
      <View
        style={[styles.activityCard, { backgroundColor: theme.colors.surface }]}
      >
        {/* Header */}
        <View style={styles.activityHeader}>
          <View style={styles.activityInfo}>
            <View style={styles.activityTitleRow}>
              <Ionicons
                name={getActivityTypeIcon(item.type)}
                size={20}
                color={theme.colors.primary}
              />
              <Text
                style={[styles.activityTitle, { color: theme.colors.text }]}
              >
                {item.title}
              </Text>
              {item.aiGenerated && (
                <View
                  style={[
                    styles.aiTag,
                    { backgroundColor: theme.colors.primaryLight },
                  ]}
                >
                  <Text
                    style={[styles.aiTagText, { color: theme.colors.primary }]}
                  >
                    AI
                  </Text>
                </View>
              )}
            </View>
            <Text
              style={[
                styles.activityDescription,
                { color: theme.colors.textSecondary },
              ]}
            >
              {item.description}
            </Text>
          </View>
        </View>

        {/* Activity Stats */}
        <View style={styles.activityStats}>
          <View style={styles.statItem}>
            <Ionicons
              name='time-outline'
              size={16}
              color={theme.colors.textSecondary}
            />
            <Text
              style={[styles.statText, { color: theme.colors.textSecondary }]}
            >
              {formatDuration(item.estimatedDuration)}
            </Text>
          </View>
          <View style={styles.statItem}>
            <Ionicons
              name='people-outline'
              size={16}
              color={theme.colors.textSecondary}
            />
            <Text
              style={[styles.statText, { color: theme.colors.textSecondary }]}
            >
              {item.optimalGroupSize[0]}-{item.optimalGroupSize[1]} people
            </Text>
          </View>
          <View style={styles.statItem}>
            <View
              style={[
                styles.difficultyDot,
                { backgroundColor: difficultyColor },
              ]}
            />
            <Text style={[styles.statText, { color: difficultyColor }]}>
              {difficultyLabel}
            </Text>
          </View>
        </View>

        {/* Skill Alignment */}
        <View style={styles.skillAlignment}>
          <Text
            style={[
              styles.alignmentLabel,
              { color: theme.colors.textSecondary },
            ]}
          >
            Group skill alignment:
          </Text>
          <ConfidenceIndicator
            confidence={item.groupSkillAlignment}
            size='small'
            showLabel={true}
            label={`${Math.round(item.groupSkillAlignment * 100)}%`}
          />
        </View>

        {/* Required Skills */}
        {item.requiredSkills.length > 0 && (
          <View style={styles.requiredSkills}>
            <Text
              style={[
                styles.skillsLabel,
                { color: theme.colors.textSecondary },
              ]}
            >
              Skills practiced:
            </Text>
            <View style={styles.skillTags}>
              {item.requiredSkills.slice(0, 3).map((skill, index) => (
                <View
                  key={index}
                  style={[
                    styles.skillTag,
                    { backgroundColor: theme.colors.successLight },
                  ]}
                >
                  <Text
                    style={[styles.skillText, { color: theme.colors.success }]}
                  >
                    {skill.replace(/_/g, ' ')}
                  </Text>
                </View>
              ))}
              {item.requiredSkills.length > 3 && (
                <Text
                  style={[
                    styles.moreSkills,
                    { color: theme.colors.textSecondary },
                  ]}
                >
                  +{item.requiredSkills.length - 3} more
                </Text>
              )}
            </View>
          </View>
        )}

        {/* Engagement Prediction */}
        <View style={styles.engagementPrediction}>
          <Ionicons name='trending-up' size={16} color={theme.colors.primary} />
          <Text
            style={[styles.engagementText, { color: theme.colors.primary }]}
          >
            {Math.round(item.engagementPrediction * 100)}% predicted engagement
          </Text>
        </View>

        {/* Actions */}
        <View style={styles.activityActions}>
          <Button
            title='Start Activity'
            onPress={() => onActivityStart(item.id)}
            variant='primary'
            size='small'
            icon='play'
          />
          <TouchableOpacity
            style={[styles.detailsButton, { borderColor: theme.colors.border }]}
            onPress={() => setShowActivityDetails(item.id)}
          >
            <Text
              style={[
                styles.detailsButtonText,
                { color: theme.colors.primary },
              ]}
            >
              Details
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  const renderActivityDetails = () => {
    const activity = activities.find(a => a.id === showActivityDetails);
    if (!activity) return null;

    return (
      <Modal
        visible={!!showActivityDetails}
        animationType='slide'
        presentationStyle='pageSheet'
      >
        <View
          style={[
            styles.modalContainer,
            { backgroundColor: theme.colors.background },
          ]}
        >
          <View style={styles.modalHeader}>
            <TouchableOpacity
              onPress={() => setShowActivityDetails(null)}
              style={styles.modalCloseButton}
            >
              <Text
                style={[styles.modalCloseText, { color: theme.colors.primary }]}
              >
                Close
              </Text>
            </TouchableOpacity>
            <Text style={[styles.modalTitle, { color: theme.colors.text }]}>
              Activity Details
            </Text>
            <View style={styles.modalPlaceholder} />
          </View>

          <View style={styles.modalContent}>
            <Text style={[styles.detailTitle, { color: theme.colors.text }]}>
              {activity.title}
            </Text>
            <Text
              style={[
                styles.detailDescription,
                { color: theme.colors.textSecondary },
              ]}
            >
              {activity.description}
            </Text>

            {/* Objectives */}
            <View style={styles.detailSection}>
              <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
                Learning Objectives
              </Text>
              {activity.objectives.map((objective, index) => (
                <View key={index} style={styles.objectiveItem}>
                  <Ionicons
                    name='checkmark'
                    size={16}
                    color={theme.colors.success}
                  />
                  <Text
                    style={[
                      styles.objectiveText,
                      { color: theme.colors.textSecondary },
                    ]}
                  >
                    {objective}
                  </Text>
                </View>
              ))}
            </View>

            {/* Materials */}
            {activity.materials.length > 0 && (
              <View style={styles.detailSection}>
                <Text
                  style={[styles.sectionTitle, { color: theme.colors.text }]}
                >
                  Required Materials
                </Text>
                {activity.materials.map((material, index) => (
                  <View key={index} style={styles.materialItem}>
                    <Ionicons
                      name='cube-outline'
                      size={16}
                      color={theme.colors.primary}
                    />
                    <Text
                      style={[
                        styles.materialText,
                        { color: theme.colors.textSecondary },
                      ]}
                    >
                      {material}
                    </Text>
                  </View>
                ))}
              </View>
            )}

            {/* AI Adaptation Info */}
            {activity.adaptedForGroup && (
              <View
                style={[
                  styles.aiAdaptationInfo,
                  { backgroundColor: theme.colors.primaryLight },
                ]}
              >
                <Ionicons name='bulb' size={20} color={theme.colors.primary} />
                <Text
                  style={[
                    styles.aiAdaptationText,
                    { color: theme.colors.primary },
                  ]}
                >
                  This activity has been AI-adapted for your group's skill
                  levels and learning goals
                </Text>
              </View>
            )}
          </View>
        </View>
      </Modal>
    );
  };

  if (loading.activities || generatingActivities) {
    return (
      <View style={styles.loadingContainer}>
        <LoadingSpinner />
        <Text
          style={[styles.loadingText, { color: theme.colors.textSecondary }]}
        >
          Generating collaborative learning activities...
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={[styles.headerTitle, { color: theme.colors.text }]}>
          Collaborative Learning Activities
        </Text>
        {showGenerateButton && (
          <Button
            title='Generate New'
            onPress={handleGenerateActivities}
            variant='outline'
            size='small'
            icon='refresh'
            loading={generatingActivities}
          />
        )}
      </View>

      {/* Activity Type Filter */}
      <View style={styles.typeFilter}>
        <FlatList
          data={activityTypes}
          renderItem={({ item }) => (
            <TouchableOpacity
              style={[
                styles.typeButton,
                selectedType === item.key && {
                  backgroundColor: theme.colors.primaryLight,
                },
              ]}
              onPress={() => setSelectedType(item.key)}
            >
              <Ionicons
                name={item.icon as any}
                size={16}
                color={
                  selectedType === item.key
                    ? theme.colors.primary
                    : theme.colors.textSecondary
                }
              />
              <Text
                style={[
                  styles.typeButtonText,
                  {
                    color:
                      selectedType === item.key
                        ? theme.colors.primary
                        : theme.colors.textSecondary,
                  },
                ]}
              >
                {item.label}
              </Text>
            </TouchableOpacity>
          )}
          keyExtractor={item => item.key}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.typeFilterContent}
        />
      </View>

      {/* Error Display */}
      {error && (
        <ErrorMessage message={error} onRetry={handleGenerateActivities} />
      )}

      {/* Activities List */}
      <FlatList
        data={getFilteredActivities()}
        renderItem={renderActivityItem}
        keyExtractor={item => item.id}
        contentContainerStyle={styles.listContainer}
        showsVerticalScrollIndicator={false}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Ionicons
              name='school-outline'
              size={64}
              color={theme.colors.textSecondary}
            />
            <Text
              style={[styles.emptyText, { color: theme.colors.textSecondary }]}
            >
              No activities available
            </Text>
            <Text
              style={[
                styles.emptySubtext,
                { color: theme.colors.textSecondary },
              ]}
            >
              Generate AI-powered collaborative learning activities for your
              group
            </Text>
            {showGenerateButton && (
              <Button
                title='Generate Activities'
                onPress={handleGenerateActivities}
                variant='primary'
                size='medium'
                icon='add'
                style={styles.generateButton}
              />
            )}
          </View>
        }
      />

      {/* Activity Details Modal */}
      {renderActivityDetails()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  typeFilter: {
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  typeFilterContent: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  typeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    marginRight: 12,
  },
  typeButtonText: {
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 6,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 32,
  },
  loadingText: {
    fontSize: 16,
    marginTop: 16,
  },
  listContainer: {
    padding: 16,
  },
  activityCard: {
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  activityHeader: {
    marginBottom: 12,
  },
  activityInfo: {
    flex: 1,
  },
  activityTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  activityTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginLeft: 8,
    flex: 1,
  },
  aiTag: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
  },
  aiTagText: {
    fontSize: 10,
    fontWeight: '600',
  },
  activityDescription: {
    fontSize: 14,
    lineHeight: 20,
  },
  activityStats: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
  },
  statText: {
    fontSize: 12,
    marginLeft: 4,
  },
  difficultyDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginRight: 4,
  },
  skillAlignment: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  alignmentLabel: {
    fontSize: 14,
  },
  requiredSkills: {
    marginBottom: 12,
  },
  skillsLabel: {
    fontSize: 14,
    marginBottom: 8,
  },
  skillTags: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  skillTag: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 8,
    marginBottom: 4,
  },
  skillText: {
    fontSize: 12,
    fontWeight: '500',
    textTransform: 'capitalize',
  },
  moreSkills: {
    fontSize: 12,
    fontStyle: 'italic',
  },
  engagementPrediction: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  engagementText: {
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 6,
  },
  activityActions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  detailsButton: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  detailsButtonText: {
    fontSize: 14,
    fontWeight: '500',
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 64,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
    textAlign: 'center',
    paddingHorizontal: 32,
    marginBottom: 24,
  },
  generateButton: {
    marginTop: 16,
  },
  modalContainer: {
    flex: 1,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  modalCloseButton: {
    padding: 4,
  },
  modalCloseText: {
    fontSize: 16,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  modalPlaceholder: {
    width: 60,
  },
  modalContent: {
    flex: 1,
    padding: 16,
  },
  detailTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: 8,
  },
  detailDescription: {
    fontSize: 16,
    lineHeight: 24,
    marginBottom: 24,
  },
  detailSection: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  objectiveItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  objectiveText: {
    fontSize: 14,
    lineHeight: 20,
    marginLeft: 8,
    flex: 1,
  },
  materialItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  materialText: {
    fontSize: 14,
    marginLeft: 8,
  },
  aiAdaptationInfo: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 12,
    borderRadius: 8,
    marginTop: 16,
  },
  aiAdaptationText: {
    fontSize: 14,
    lineHeight: 20,
    marginLeft: 8,
    flex: 1,
  },
});
